import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Clock, Facebook, Twitter, Linkedin, Mail, List } from "lucide-react";
import SEO, { articleSchema, faqSchema, breadcrumbSchema } from "../components/SEO";
import { Breadcrumbs, CTASection } from "../components/Shared";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { BRAND } from "../data/siteData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    setNotFound(false);
    axios.get(`${API}/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
    axios.get(`${API}/posts`).then((r) => setRelated(r.data.filter((p) => p.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  const toc = useMemo(() => {
    if (!post) return [];
    return [...post.content.matchAll(/^## (.+)$/gm)].map((m) => ({ text: m[1], id: slugify(m[1]) }));
  }, [post]);

  if (notFound) {
    return (
      <div className="bg-white min-h-[50vh] flex flex-col items-center justify-center gap-4 py-32">
        <h1 className="font-display text-3xl font-bold text-[#000000]">Article not found</h1>
        <Link to="/blog" className="text-[#028090] font-bold uppercase tracking-wider text-sm">Back to Blog</Link>
      </div>
    );
  }
  if (!post) return <div className="bg-white min-h-[50vh] flex items-center justify-center py-32 text-gray-400">Loading…</div>;

  const crumbs = [{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${post.slug}` }];
  const shareUrl = encodeURIComponent(`${BRAND.domain}/blog/${post.slug}`);
  const shareTitle = encodeURIComponent(post.title);

  return (
    <>
      <SEO
        title={post.meta_title}
        description={post.meta_description}
        path={`/blog/${post.slug}`}
        ogImage={post.image}
        schemas={[articleSchema(post), post.faqs?.length ? faqSchema(post.faqs) : null, breadcrumbSchema(crumbs)]}
      />

      <section className="hero-gradient py-16 md:py-20 px-6 md:px-12 grain relative" data-testid="post-hero">
        <div className="max-w-4xl mx-auto">
          <Breadcrumbs items={crumbs} />
          <span className="bg-[#028090]/20 text-[#5fc7d4] text-xs font-bold uppercase tracking-wider px-3 py-1.5">{post.category}</span>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mt-5 leading-tight">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-5 mt-6 text-sm text-gray-400">
            <span>{post.author_name}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {post.read_time} min read</span>
            <span>{new Date(post.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          </div>
          <div className="flex items-center gap-3 mt-6" data-testid="post-share-buttons">
            <span className="text-xs text-gray-500 uppercase tracking-wider">Share</span>
            <a data-testid="share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className="text-gray-400 hover:text-[#028090] transition-colors"><Facebook size={17} strokeWidth={1.5} /></a>
            <a data-testid="share-twitter" href={`https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`} target="_blank" rel="noopener noreferrer" aria-label="Share on X" className="text-gray-400 hover:text-[#028090] transition-colors"><Twitter size={17} strokeWidth={1.5} /></a>
            <a data-testid="share-linkedin" href={`https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className="text-gray-400 hover:text-[#028090] transition-colors"><Linkedin size={17} strokeWidth={1.5} /></a>
            <a data-testid="share-email" href={`mailto:?subject=${shareTitle}&body=${shareUrl}`} aria-label="Share by email" className="text-gray-400 hover:text-[#028090] transition-colors"><Mail size={17} strokeWidth={1.5} /></a>
          </div>
        </div>
      </section>

      <article className="bg-white py-16 md:py-20 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <img src={post.image} alt={post.image_alt} className="w-full aspect-[16/9] object-cover mb-10" />

          {toc.length > 1 && (
            <nav className="border border-gray-200 bg-gray-50 p-6 mb-10" data-testid="post-toc">
              <p className="flex items-center gap-2 font-display font-bold text-[#000000] mb-3 text-sm uppercase tracking-wider"><List size={16} className="text-[#028090]" /> Table of Contents</p>
              <ul className="space-y-2">
                {toc.map((t) => (
                  <li key={t.id}><a href={`#${t.id}`} className="text-[#028090] text-sm hover:underline">{t.text}</a></li>
                ))}
              </ul>
            </nav>
          )}

          <div className="prose-blog" data-testid="post-content">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h2: ({ children }) => <h2 id={slugify(String(children))}>{children}</h2>,
                a: ({ href, children }) =>
                  href?.startsWith("/") ? <Link to={href} className="text-[#028090] font-semibold underline underline-offset-2">{children}</Link> : <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {post.faqs?.length > 0 && (
            <div className="mt-14 border-t border-gray-200 pt-10" data-testid="post-faq-section">
              <h2 className="font-display text-2xl font-bold text-[#000000] mb-6">Frequently Asked Questions</h2>
              <Accordion type="single" collapsible>
                {post.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`pfaq-${i}`}>
                    <AccordionTrigger data-testid={`post-faq-trigger-${i}`} className="text-left font-display font-semibold text-[#000000]">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-gray-600 leading-relaxed">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          )}

          <div className="mt-14 bg-[#000000] p-8 flex flex-col sm:flex-row gap-5 items-start" data-testid="post-author-bio">
            <div className="w-14 h-14 bg-[#028090] flex items-center justify-center font-display font-extrabold text-white text-xl shrink-0">V</div>
            <div>
              <p className="font-display font-bold text-white">{post.author_name}</p>
              <p className="text-gray-400 text-sm mt-1.5 leading-relaxed">{post.author_bio}</p>
            </div>
          </div>

          {post.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-8">
              {post.tags.map((t) => <span key={t} className="text-xs bg-gray-100 text-gray-600 px-3 py-1.5">#{t}</span>)}
            </div>
          )}

          {related.length > 0 && (
            <div className="mt-16 border-t border-gray-200 pt-10" data-testid="related-posts">
              <h2 className="font-display text-2xl font-bold text-[#000000] mb-6">Related Articles</h2>
              <div className="grid sm:grid-cols-3 gap-6">
                {related.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`related-post-${r.slug}`} className="group border border-gray-200 hover:border-[#028090] transition-colors duration-200">
                    <img src={r.image} alt={r.image_alt} loading="lazy" className="w-full aspect-[16/10] object-cover" />
                    <p className="font-display font-semibold text-sm text-[#000000] group-hover:text-[#028090] transition-colors p-4 leading-snug">{r.title}</p>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>

      <CTASection title="Ready for the Floor This Article Describes?" />
    </>
  );
}
